import React, { useState, useEffect } from 'react';
import { Download, FileText, FileSpreadsheet, File, Clock, CheckCircle, AlertCircle, X } from 'lucide-react';
import { apiClient } from '../../services/api';

interface ExportDashboardProps {
  onExport: (exportData: any) => Promise<any>;
  onGetExportJobs: () => Promise<any[]>;
  onGetReportTemplates: () => Promise<any[]>;
  onGetScheduledReports: () => Promise<any[]>;
}

const ExportDashboard: React.FC<ExportDashboardProps> = ({
  onExport,
  onGetExportJobs,
  onGetReportTemplates,
  onGetScheduledReports
}) => {
  const [exportJobs, setExportJobs] = useState<any[]>([]);
  const [reportTemplates, setReportTemplates] = useState<any[]>([]);
  const [scheduledReports, setScheduledReports] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showExportForm, setShowExportForm] = useState(false);
  const [exportForm, setExportForm] = useState({
    export_type: 'csv',
    filters: {},
    format_options: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      console.log('Loading export data...');
      const [jobs, templates, reports] = await Promise.all([
        onGetExportJobs(),
        onGetReportTemplates(),
        onGetScheduledReports()
      ]);
      console.log('Export jobs received:', jobs);
      console.log('Report templates received:', templates);
      console.log('Scheduled reports received:', reports);
      setExportJobs(jobs || []);
      setReportTemplates(templates || []);
      setScheduledReports(reports || []);
    } catch (error) {
      console.error('Error loading export data:', error);
      // Set empty arrays on error to prevent undefined access
      setExportJobs([]);
      setReportTemplates([]);
      setScheduledReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsLoading(true);
      const result = await onExport(exportForm);
      setExportJobs(prev => [...prev, result]);
      setShowExportForm(false);
    } catch (error) {
      console.error('Error creating export:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (job: any) => {
    try {
      const jobId = job.id || job.job_id;
      
      // Use the API client to download the file
      const response = await apiClient.get(`/analytics/export/download/${jobId}/`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = job.file_name || `workshop_export_${jobId}.${job.export_type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'processing':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getFileIcon = (exportType: string) => {
    switch (exportType) {
      case 'csv':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
      case 'pdf':
        return <File className="w-5 h-5 text-red-600" />;
      default:
        return <File className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Export Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Data Export</h3>
          <div className="flex space-x-2">
            <button
              onClick={loadData}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowExportForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>New Export</span>
            </button>
          </div>
        </div>

        {/* Export Form */}
        {showExportForm && (
          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Export Format
                </label>
                <select
                  value={exportForm.export_type}
                  onChange={(e) => setExportForm(prev => ({ ...prev, export_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="csv">CSV</option>
                  <option value="excel">Excel</option>
                  <option value="pdf">PDF</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                  <option value="all">All time</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Include Fields
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All fields</option>
                  <option value="basic">Basic fields only</option>
                  <option value="financial">Financial data only</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowExportForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Create Export'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Jobs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Exports</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {exportJobs.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <div className="mb-4">
                <Download className="w-12 h-12 mx-auto text-gray-400" />
              </div>
              <p className="text-lg font-medium mb-2">No export jobs found</p>
              <p className="text-sm">Create your first export to get started</p>
            </div>
          ) : (
            exportJobs.map((job: any) => (
              <div key={job?.job_id || job?.id || Math.random()} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getFileIcon(job?.export_type || 'csv')}
                    <div>
                      <div className="font-medium text-gray-900">
                        Workshop Data Export
                      </div>
                      <div className="text-sm text-gray-500">
                        {(job?.export_type || 'csv').toUpperCase()} • {job?.created_at ? new Date(job.created_at).toLocaleDateString() : 'Unknown date'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(job?.status || 'pending')}`}>
                      <div className="flex items-center space-x-1">
                        {getStatusIcon(job?.status || 'pending')}
                        <span className="capitalize">{job?.status || 'pending'}</span>
                      </div>
                    </div>
                    
                    {job?.status === 'completed' && (
                      <button 
                        onClick={() => handleDownload(job)}
                        className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Download
                      </button>
                    )}
                    
                    {job?.status === 'failed' && (
                      <button className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                        Retry
                      </button>
                    )}
                  </div>
                </div>
                
                {job?.status === 'processing' && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{job?.progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${job?.progress || 0}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Report Templates */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Report Templates</h3>
        </div>
        <div className="p-6">
          {reportTemplates.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">No report templates</p>
              <p className="text-sm">Create templates to standardize your reports</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportTemplates.map((template: any) => (
              <div key={template?.id || Math.random()} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{template?.name || 'Unnamed Template'}</h4>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {template?.category || 'General'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template?.description || 'No description available'}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {template?.fields?.length || 0} fields
                  </span>
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                    Use Template
                  </button>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>

      {/* Scheduled Reports */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Scheduled Reports</h3>
        </div>
        <div className="p-6">
          {scheduledReports.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No scheduled reports</p>
              <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                Create Scheduled Report
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledReports.map((report: any) => (
                <div key={report?.id || Math.random()} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{report?.name || 'Unnamed Report'}</div>
                    <div className="text-sm text-gray-600">
                      {report?.frequency || 'Unknown'} • {report?.recipients?.length || 0} recipients
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      report?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {report?.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportDashboard;